/**
 * Typed error model. ApiError is thrown for every non-2xx response.
 * Callers switch on `code`.
 *
 * The union grows as wsr-api/saas-auth surface new codes in later specs.
 * Unknown backend codes map to `UNKNOWN` with the original string preserved
 * in `details.raw`. This file is saas-auth's OWN error model (the
 * `{success:false,error:{code,message}}` envelope `apiErrorFromEnvelope`
 * parses) — codes native to that envelope belong here, additively
 * (WSRPDM-0018 added the six below). Contrast `src/api/wsr/errors.ts`: that
 * surface's envelope shape is foreign to this parser (flat, not wrapped), so
 * it stays local to `src/api/wsr/`.
 */

export type ErrorCode =
  // Auth round-trip / session
  | 'INVALID_RETURN_URL'
  | 'SESSION_EXPIRED'
  | 'CSRF_VALIDATION_FAILED'
  | 'INVALID_CREDENTIALS'
  // Platform access
  | 'PLATFORM_ADMIN_REQUIRED'
  | 'TENANT_NOT_FOUND'
  // Cross-cutting
  | 'TENANT_ORIGIN_MISMATCH'
  | 'RATE_LIMIT_REJECTED'
  | 'INVALID_LOCALE'
  // Product-admin identity (WSRPDM-0018 — tenant create, members, invitations)
  | 'RESERVED_SLUG'
  | 'HOSTNAME_ALREADY_REGISTERED'
  | 'ALREADY_ACTIVE_MEMBER'
  | 'INVITATION_ALREADY_PENDING'
  | 'LAST_OWNER_PROTECTED'
  | 'ROLE_CHANGE_CROSSES_TABLES'
  | 'INVITATION_ALREADY_ACCEPTED'
  // UI-synthesized
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

export interface BackendErrorEnvelope {
  success: false;
  error: {
    code: string;
    message?: string;
  };
}

export interface ApiErrorDetails {
  message?: string;
  raw?: unknown;
}

export class ApiError extends Error {
  public readonly code: ErrorCode;
  public readonly status: number;
  public readonly requestId: string | undefined;
  public readonly details: ApiErrorDetails;

  constructor(
    code: ErrorCode,
    status: number,
    options: { requestId?: string | undefined; details?: ApiErrorDetails } = {},
  ) {
    super(`API error: ${code}`);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.requestId = options.requestId;
    this.details = options.details ?? {};
  }
}

const SHIPPED_CODES: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'INVALID_RETURN_URL',
  'CSRF_VALIDATION_FAILED',
  'INVALID_CREDENTIALS',
  'PLATFORM_ADMIN_REQUIRED',
  'TENANT_NOT_FOUND',
  'TENANT_ORIGIN_MISMATCH',
  'RATE_LIMIT_REJECTED',
  'INVALID_LOCALE',
  'RESERVED_SLUG',
  'HOSTNAME_ALREADY_REGISTERED',
  'ALREADY_ACTIVE_MEMBER',
  'INVITATION_ALREADY_PENDING',
  'LAST_OWNER_PROTECTED',
  'ROLE_CHANGE_CROSSES_TABLES',
  'INVITATION_ALREADY_ACCEPTED',
]);

export function mapBackendCode(code: string | undefined): ErrorCode {
  if (!code) return 'UNKNOWN';
  if (SHIPPED_CODES.has(code as ErrorCode)) return code as ErrorCode;
  if (code === 'INVALID_CSRF') return 'CSRF_VALIDATION_FAILED';
  if (code === 'INVALID_ORIGIN') return 'TENANT_ORIGIN_MISMATCH';
  if (code === 'SESSION_EXPIRED') return 'SESSION_EXPIRED';
  return 'UNKNOWN';
}

export function apiErrorFromEnvelope(status: number, body: unknown, requestId?: string): ApiError {
  const envelope = body as Partial<BackendErrorEnvelope> | null;
  const code = mapBackendCode(envelope?.error?.code);
  const details: ApiErrorDetails = { raw: body };
  if (envelope?.error?.message !== undefined) details.message = envelope.error.message;
  return new ApiError(code, status, { requestId, details });
}
